/**
 * Pure viewport-clamping for the planner's custom fixed-position tooltips.
 *
 * The planner tooltips are positioned with `position: fixed` from a trigger's
 * bounding rect (no Radix/floating-ui, because the custom portal carries
 * hover-bridge, pin-on-cut and scroll-follow behavior). Without clamping they get
 * cut off at the viewport edges — worst at the bottom, where a trigger near the
 * page bottom pushes the tooltip off-screen.
 *
 * This helper keeps the tooltip fully on-screen: it places the tooltip BELOW the
 * trigger by default, flips it ABOVE when there isn't room below (and there's more
 * room above), then clamps both axes into the visible area.
 */
export interface ClampTooltipParams {
  /** The trigger element's bounding rect (viewport coordinates). */
  triggerRect: { top: number; bottom: number; left: number };
  /** Desired left edge before clamping (e.g. triggerRect.left + alignOffset). */
  desiredLeft: number;
  /** Measured tooltip width in px (must be > 0). */
  tooltipWidth: number;
  /** Measured tooltip height in px (must be > 0). */
  tooltipHeight: number;
  /** Gap between the trigger and the tooltip in px. */
  gap: number;
  /** Viewport size in px. */
  viewport: { width: number; height: number };
  /** Minimum distance to keep from every viewport edge in px. */
  padding: number;
}

export interface ClampTooltipResult {
  left: number;
  top: number;
  /** True when the tooltip was flipped to sit ABOVE the trigger. */
  flippedAbove: boolean;
}

const clamp = (value: number, min: number, max: number): number =>
  // Math.max(min, ...) wins when the tooltip is larger than the available span
  // (min > max), pinning it to the leading edge instead of overshooting.
  Math.max(min, Math.min(value, max));

export const clampTooltipPosition = ({
  triggerRect,
  desiredLeft,
  tooltipWidth,
  tooltipHeight,
  gap,
  viewport,
  padding,
}: ClampTooltipParams): ClampTooltipResult => {
  // --- Vertical: prefer below the trigger, flip above when it doesn't fit. ---
  const belowTop = triggerRect.bottom + gap;
  const aboveTop = triggerRect.top - gap - tooltipHeight;

  const overflowsBelow = belowTop + tooltipHeight > viewport.height - padding;
  const roomAbove = triggerRect.top - gap - padding;
  const flippedAbove = overflowsBelow && roomAbove >= tooltipHeight;

  const rawTop = flippedAbove ? aboveTop : belowTop;
  const top = clamp(rawTop, padding, viewport.height - tooltipHeight - padding);

  // --- Horizontal: clamp the desired left into the visible area. ---
  const left = clamp(desiredLeft, padding, viewport.width - tooltipWidth - padding);

  return { left, top, flippedAbove };
};
